import { FC, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrency, formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';
import { Partner } from 'types';
import {
  TradingOfferOperation,
  TradingOfferOperationStatus,
} from 'types/TradingOfferOperation';

import OperationMenuDots from 'modules/_shared/components/MenuDots/OperationMenuDots';

import { TradingOffer } from 'types/TradingOffer';

import {
  statusBadgeMap,
  menuStatusOptions,
} from '../constants/tradingOfferOperationStatus';
import getOperationReference from '../utils/getOperationReference';
import getOfferReference from '../utils/getOfferReference';

type EquityMarketAdminPanelOperationsTableProps = {
  operations: TradingOfferOperation[];
  offers?: TradingOffer[];
  onViewOperationDetail: (operationId: string) => void;
  onUpdateOperationStatus: (
    operationId: string,
    status: string,
    comment?: string
  ) => void;
  embedded?: boolean;
};

const getPartyName = (party: string | Partial<Partner>) => {
  if (typeof party === 'string') return '-';
  return party?.name || '-';
};

const ALL_STATUSES = 'ALL';

const statusFilterOptions: { value: string; label: string }[] = [
  { value: ALL_STATUSES, label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'KYC_REVIEW', label: 'KYCReview' },
  { value: 'VALIDATION', label: 'PendingValidation' },
  { value: 'PAYMENT_PENDING', label: 'PaymentPending' },
  { value: 'TRANSFER_PENDING', label: 'TransferPending' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REJECTED', label: 'Rejected' },
];

const EquityMarketAdminPanelOperationsTable: FC<
  EquityMarketAdminPanelOperationsTableProps
> = ({
  operations,
  offers = [],
  onViewOperationDetail,
  onUpdateOperationStatus,
  embedded = false,
}) => {
  const { t } = useTranslate();
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [offerFilter, setOfferFilter] = useState(ALL_STATUSES);

  const offerFilterOptions = useMemo(() => {
    const offerIds = new Set(operations.map((op) => op.tradingOffer));
    const relevantOffers = offers.filter((o) => offerIds.has(o._id));

    return [
      { value: ALL_STATUSES, label: t('AllOffers') },
      ...relevantOffers.map((o) => ({
        value: o._id,
        label: getOfferReference(o),
      })),
    ];
  }, [offers, operations, t]);

  const filteredOperations = useMemo(() => {
    let result = operations;
    if (offerFilter !== ALL_STATUSES) {
      result = result.filter((op) => op.tradingOffer === offerFilter);
    }
    if (statusFilter !== ALL_STATUSES) {
      result = result.filter((op) => op.status === statusFilter);
    }
    return result;
  }, [operations, statusFilter, offerFilter]);

  const isTerminalStatus = (status: TradingOfferOperationStatus) =>
    status === 'COMPLETED' || status === 'CANCELLED' || status === 'REJECTED';

  const content = (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <div className="d-flex align-items-center mr-4">
            <label className="form-label mb-0 mr-1 text-soft small">
              {t('Offers')}:
            </label>
            <select
              className="custom-select custom-select-sm"
              style={{ minWidth: '160px' }}
              value={offerFilter}
              onChange={(e) => setOfferFilter(e.target.value)}
            >
              {offerFilterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="d-flex align-items-center">
            <label className="form-label mb-0 mr-1 text-soft small">
              {t('Status')}:
            </label>
            <select
              className="custom-select custom-select-sm"
              style={{ minWidth: '160px' }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {statusFilterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.label)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {filteredOperations.length === 0 ? (
        <div className="text-center py-4">
          <em className="icon ni ni-inbox fs-3 text-soft d-block mb-2" />
          <p className="text-soft">{t('NoOperationsAvailable')}</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-tranx table-hover mb-0">
            <thead>
              <tr className="tb-tnx-head">
                <th>{t('Ref')}</th>
                <th>{t('Date')}</th>
                <th>{t('Buyer')}</th>
                <th>{t('Seller')}</th>
                <th className="text-right">{t('Shares')}</th>
                <th className="text-right">{t('Amount')}</th>
                <th>{t('Status')}</th>
                <th className="text-right">{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOperations.map((op) => {
                const badge =
                  statusBadgeMap[op.status] || statusBadgeMap.PENDING;

                return (
                  <tr
                    key={op._id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onViewOperationDetail(op._id)}
                  >
                    <td>
                      <span className="ref text-base badge badge-light">
                        {getOperationReference(op)}
                      </span>
                    </td>
                    <td>{format(new Date(op.requestDate), dateFormat)}</td>
                    <td>
                      <span className="d-inline-flex align-items-center">
                        <em className="icon ni ni-user mr-1 text-soft" />
                        {getPartyName(op.buyer)}
                      </span>
                    </td>
                    <td>
                      <span className="d-inline-flex align-items-center">
                        <em className="icon ni ni-user mr-1 text-soft" />
                        {getPartyName(op.seller)}
                      </span>
                    </td>
                    <td className="text-right">
                      {formatNumber(op.sharesCount)}
                    </td>
                    <td className="text-right">
                      <strong>{formatCurrency(op.totalPrice)}</strong>
                    </td>
                    <td>
                      <span className={`badge badge-dim ${badge.className}`}>
                        <em className={`icon ni ${badge.icon} mr-1`} />
                        {t(badge.label)}
                      </span>
                    </td>
                    <td
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!isTerminalStatus(op.status) ? (
                        // @ts-ignore
                        <OperationMenuDots
                          title={t('ManageOperation')}
                          menuOptions={[
                            ...menuStatusOptions.map((status) => ({
                              icon: status.icon,
                              text: t(status.key),
                              action: () =>
                                onUpdateOperationStatus(op._id, status.key),
                              dropdown: true,
                            })),
                            {
                              icon: 'ni-cross-circle',
                              text: t('Rejected'),
                              action: () =>
                                onUpdateOperationStatus(op._id, 'REJECTED'),
                              dropdown: true,
                              borderTop: true,
                            },
                            {
                              icon: 'ni-na',
                              text: t('Cancelled'),
                              action: () =>
                                onUpdateOperationStatus(op._id, 'CANCELLED'),
                              dropdown: true,
                            },
                          ]}
                        />
                      ) : (
                        <button
                          type="button"
                          className="btn btn-icon btn-trigger btn-sm"
                          title={t('ViewDetail')}
                          onClick={() => onViewOperationDetail(op._id)}
                        >
                          <em className="icon ni ni-arrow-right" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="card card-bordered">
      <div className="card-inner">{content}</div>
    </div>
  );
};

export default EquityMarketAdminPanelOperationsTable;
